import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { Box } from '@mui/material';
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import {
  IssueClassifySurface,
  IssueReportRecord,
} from '../../redux/features/issueReport/issueReport.props';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { DISCARD_BUTTON_SX } from './issueActionButtonStyle';
import IssueClassifyBody, { IssueClassifyHandle } from './IssueClassifyBody';
import { formatElapsed, isRealSessionId, shortId } from './issueReportFormat';

const SEPARATOR_SX = {
  flexShrink: 0,
  color: 'rgba(255,255,255,0.32)',
} as const;

/**
 * The hairline between two reports.
 *
 * Rows lost their store and session, which is what used to make each one a dense
 * block of text with obvious edges. What is left is one short id at the far left and
 * an age and a button at the far right, so without a rule the eye has nothing to
 * follow across the gap and no reason to read `(4m)` and `Discard` as belonging to
 * the id opposite them.
 *
 * White at low alpha rather than a hue: an expanded row puts the 23-chip taxonomy
 * directly underneath, in ten colours that all mean something, and a coloured divider
 * would be an eleventh that means nothing.
 */
const ROW_DIVIDER = '1px solid rgba(255,255,255,0.18)';

interface CopyableIdProps {
  /** `S` for session, `I` for issue — one character, so the row stays scannable. */
  label: string;
  value: string;
  onCopy: (value: string) => void;
  testId: string;
}

/**
 * A shortened id that copies its FULL value.
 *
 * A `<span>`, not a button, for the same reason the panel header's id chip is: three
 * copy affordances per row would put three tab stops in front of every actual action,
 * and the value is plain selectable text with the whole thing on the `title`, so
 * nothing here is keyboard-unreachable — only the one-click shortcut is.
 *
 * It stops its own click, or copying an id would also toggle the row it sits in.
 */
const CopyableId = ({ label, value, onCopy, testId }: CopyableIdProps) => (
  <Box
    component="span"
    title={value}
    data-testid={testId}
    onClick={(event: React.MouseEvent) => {
      event.stopPropagation();
      onCopy(value);
    }}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      flexShrink: 0,
      px: '3px',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
      },
    }}
  >
    <Box component="span" sx={{ color: 'rgba(255,255,255,0.45)' }}>
      {label}
    </Box>
    {shortId(value)}
    <ContentCopyRoundedIcon sx={{ fontSize: 11 }} />
  </Box>
);

interface IssueBacklogListProps {
  /** Outstanding reports, newest first — `selectOutstandingIssueReports`. */
  reports: IssueReportRecord[];
  /**
   * Which host this list is in, forwarded to `IssueClassifyBody` as
   * `classified_from`. The whole reason this component has two hosts is that the two
   * are worth judging separately, so it cannot pick a value for itself.
   */
  surface: IssueClassifySurface;
  /**
   * Injected rather than read at render time, so the ages hold still while the agent
   * reads the list and the whole thing is testable without fake timers.
   */
  nowMs: number;
  /** A row was opened. The Issue-button popover latches itself against a hover-out. */
  onRowExpand?: () => void;
}

export interface IssueBacklogListHandle {
  /**
   * Closes the open row, if there is one, and reports whether there was.
   *
   * The return value is what lets a host decide whether to CONSUME the gesture that
   * asked for this: the lobby swallows Escape only when a row was actually open, so a
   * press with the list idle stays available to everything else on the page.
   *
   * Whatever was typed in the closing row is committed by `IssueClassifyBody`'s
   * unmount commit, exactly as it is when the agent switches rows.
   */
  collapse(): boolean;
}

/**
 * HITL-1574: the outstanding reports, one row each, with the classifier expanding
 * underneath the one the agent picks.
 *
 * TWO HOSTS, one component: the popover behind the Issue button's badge, and the
 * task-router lobby card (HITL-1575). They must not drift — an agent who classifies
 * from the lobby and an agent who classifies mid-order are doing the same thing, and a
 * second implementation of it is a second place for the commit rules to go subtly
 * wrong.
 *
 * ## Rows are identified by IDS, not by content
 *
 * Deliberately not the transcript snippet or the typed detail, which is what a
 * "recognise your own report" design would show. The job here is cross-referencing
 * with support and with the logs, and the issue id is the key that does it — the
 * store and the session are already on the envelope server-side. Trade-off accepted
 * and real: the agent recognises a row by its id rather than by what it was about, so
 * the elapsed time is doing most of the work of jogging their memory. That is why it
 * is on every row rather than behind a hover.
 *
 * WHERE the report came from is shown on one host only — see `showsIdentity`.
 *
 * ## One open at a time
 *
 * The classifier is the full 23-chip taxonomy, whose one hard rule is that it never
 * scrolls — two of them open at once inside a popover would break that immediately.
 * Opening a row therefore closes any other, and the row that closes commits whatever
 * was typed in it (`IssueClassifyBody`'s unmount commit), so switching rows mid
 * sentence loses nothing.
 *
 * ## Committing or discarding removes the row, with no code here
 *
 * `selectOutstandingIssueReports` does it. A chip click amends the record so it stops
 * matching; `Discard` sets `voided`, which the selector's `!voided` clause already
 * excludes. There is no local list state to keep in step.
 *
 * ## Closing a row is the HOST's gesture, not this component's
 *
 * Which expansion is open is state only this component should hold, but *click away*
 * and *Escape* mean different things to the two hosts: in the popover they dismiss the
 * whole surface, which unmounts this. Only the lobby, where the list is the page's own
 * content and there is nothing to dismiss, needs them to mean "collapse the row". So
 * the state stays here and the gesture stays there, joined by `collapse()`.
 */
const IssueBacklogList = React.forwardRef<
  IssueBacklogListHandle,
  IssueBacklogListProps
>(({ reports, surface, nowMs, onRowExpand }, ref) => {
  const { voidIssue } = useIssueReport();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /**
   * The body of the row that is currently open — at most one, by construction.
   *
   * Needed so `Discard` on an EXPANDED row can drop the pending detail before
   * retracting. Without it the body's unmount commit would re-send the text of a
   * report that was just retracted, at a revision after the retraction.
   */
  const expandedBodyRef = useRef<IssueClassifyHandle>(null);

  /**
   * Read through a ref rather than closed over, so `collapse()` is correct when a
   * host calls it from a listener registered once — which is what a window-capture
   * Escape handler is.
   */
  const expandedIdRef = useRef<string | null>(null);
  expandedIdRef.current = expandedId;

  useImperativeHandle(
    ref,
    () => ({
      collapse: () => {
        if (!expandedIdRef.current) return false;
        setExpandedId(null);
        return true;
      },
    }),
    []
  );

  const handleToggle = useCallback(
    (issueId: string) => {
      setExpandedId((current) => (current === issueId ? null : issueId));
      onRowExpand?.();
    },
    [onRowExpand]
  );

  const handleDiscard = useCallback(
    (issueId: string) => {
      if (issueId === expandedId) {
        expandedBodyRef.current?.discard();
        setExpandedId(null);
      }
      voidIssue(issueId);
    },
    [expandedId, voidIssue]
  );

  /**
   * Hoisted out of the render rather than passed inline, so it is STABLE.
   *
   * `IssueClassifyBody` derives its `onSelect` for the memoized 23-chip grid from
   * this, so an inline arrow here re-created that callback on every list render and
   * re-rendered every chip with it — each chip's `sx` re-serialized by emotion. That
   * was invisible in the popover, which only re-renders on interaction, and became
   * real on the lobby card, which re-stamps the row ages on a timer.
   */
  const handleCommitted = useCallback(() => setExpandedId(null), []);

  /**
   * Whether a row spells out WHERE its report came from — the store and the session.
   *
   * OFF behind the Issue button. That popover is console-only, so the store is the one
   * already in the header and the session is the car the agent is serving:
   * `presto_dairy_queen_lab · no session` was the widest half of the row and told them
   * nothing they were not already looking at. `no session` was worse than redundant —
   * it says "we do not know", which is not something an agent can act on mid-order.
   *
   * ON in the lobby, which is the one surface with NO live context to be redundant
   * with: `resetSelectedRestaurantDetails` fires on the way there, no car is present,
   * and the next one can come from a different store — so the row is the only place
   * that information exists.
   *
   * Known cost, and the reason this was a decision rather than a cleanup: in the
   * console a report deferred from an earlier car — or from another store, after a
   * switch through `handleRestaurantSelect` — now renders identically to one from the
   * car on screen. The issue id still cross-references it, and the age is what
   * separates them.
   */
  const showsIdentity = surface !== 'issue_button';

  const handleCopy = useCallback((value: string) => {
    // Best effort: `navigator.clipboard` is absent outside a secure context and can
    // reject when the document is unfocused. The full value is on the `title` either
    // way, so a failed copy costs nothing and must not throw.
    navigator.clipboard?.writeText(value).catch(() => undefined);
  }, []);

  if (!reports.length) return null;

  return (
    <Box data-testid="issue-backlog-list">
      <Box
        data-testid="issue-backlog-heading"
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
          pb: '6px',
        }}
      >
        {reports.length} {reports.length === 1 ? 'issue' : 'issues'} to classify
      </Box>

      {reports.map((report, index) => {
        const isExpanded = report.issue_id === expandedId;
        const sessionId = report.envelopeIdentity.session_id;
        return (
          <Box
            key={report.issue_id}
            data-testid="issue-backlog-row"
            sx={{
              // BETWEEN reports only, never under the last one — a rule along the
              // bottom edge of the list would read as a cut-off row and invite a
              // scroll for something that is not there. On the wrapper rather than
              // the row, so an expanded row's chip grid sits inside the division
              // rather than below it.
              ...(index < reports.length - 1
                ? { borderBottom: ROW_DIVIDER }
                : null),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                py: '5px',
                px: '4px',
                borderRadius: '3px',
                fontSize: HITL3_FONT_SIZE,
                backgroundColor: isExpanded
                  ? 'rgba(255,255,255,0.10)'
                  : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {/* The identity half is ONE click target rather than three, so opening
                  a row is a press anywhere along it. The copy chips inside it stop
                  their own clicks — see below. */}
              <Box
                component="button"
                type="button"
                onClick={() => handleToggle(report.issue_id)}
                aria-expanded={isExpanded}
                data-testid="issue-backlog-row-open"
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'monospace',
                  color: 'rgba(255,255,255,0.86)',
                  '&:focus-visible': {
                    outline: `2px solid ${Colors.white}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {showsIdentity && (
                  <>
                    <Box component="span" sx={{ flexShrink: 0 }}>
                      {report.envelopeIdentity.restaurant_code ?? 'no store'}
                    </Box>

                    <Box component="span" sx={SEPARATOR_SX}>
                      ·
                    </Box>

                    {/* A flag filed with no car present froze either null or the
                        OUT_OF_SESSION sentinel, which has no hyphen and would render
                        whole in a column of 8-character ids. Both say the same
                        thing. */}
                    {isRealSessionId(sessionId) ? (
                      <CopyableId
                        label="S"
                        value={sessionId as string}
                        onCopy={handleCopy}
                        testId="issue-backlog-session-id"
                      />
                    ) : (
                      <Box
                        component="span"
                        data-testid="issue-backlog-no-session"
                        sx={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)' }}
                      >
                        no session
                      </Box>
                    )}

                    <Box component="span" sx={SEPARATOR_SX}>
                      ·
                    </Box>
                  </>
                )}

                <CopyableId
                  label="I"
                  value={report.issue_id}
                  onCopy={handleCopy}
                  testId="issue-backlog-issue-id"
                />

                {/* LAST, and in brackets: it is the one field that is about the agent
                    rather than about the record, and the thing most likely to jog
                    their memory of which problem this was. */}
                <Box
                  component="span"
                  data-testid="issue-backlog-age"
                  sx={{
                    flexShrink: 0,
                    ml: 'auto',
                    color: 'rgba(255,255,255,0.45)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ({formatElapsed(report.created_at, nowMs)})
                </Box>
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => handleDiscard(report.issue_id)}
                data-testid="issue-backlog-discard"
                // The SAME treatment the flag panel's `Discard` wears, from the one
                // module that declares it. It used to be a ghost outline here and a
                // solid button there — one word, one effect, two ways of reading it.
                sx={DISCARD_BUTTON_SX}
              >
                Discard
              </Box>
            </Box>

            {isExpanded && (
              <Box sx={{ pb: 1, pt: '2px' }}>
                <IssueClassifyBody
                  ref={expandedBodyRef}
                  record={report}
                  surface={surface}
                  // The agent clicked this row to work on it, so the caret belongs in
                  // the detail field. Safe against the omnibar because the hosting
                  // surface declares `data-caret-owner`.
                  autoFocusDetail
                  // No countdown here to race, so the one reason to bind a sending
                  // key is absent. Enter would be a keystroke that silently ends the
                  // agent's only visit to a report they deferred once already.
                  enterSubmits={false}
                  onCommitted={handleCommitted}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

export default IssueBacklogList;
